import type { FirebaseApp } from 'firebase/app';
import {
  Timestamp,
  collection,
  documentId,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
  type Firestore,
  type QueryConstraint,
} from 'firebase/firestore';
import {
  connectFunctionsEmulator,
  getFunctions,
  httpsCallable,
  type Functions,
} from 'firebase/functions';
import {
  AUDIT_EVENTS_COLLECTION,
  AuditEventSchema,
  actionDApp,
  type AuditCategory,
  type AuditEvent,
  type AuditMeta,
} from '@taki/data-model';
import type { EmulatorEndpoint } from '@taki/runtime-config';

/**
 * LE JOURNAL D'AUDIT, COTE APP (`orgs/{orgId}/auditEvents`, `docs/JOURNAL.md`).
 *
 * Deux verbes, et pas un de plus :
 *   - `list` LIT le journal, page par page. Les rules ne l'ouvrent qu'au
 *     PROPRIETAIRE actif : pour tout autre compte, la promesse est rejetee
 *     (`permission-denied`), et c'est la reponse juste ;
 *   - `noter` demande au serveur d'ECRIRE un evenement d'app
 *     (`app.<appId>.<verbe>`). Le client n'ecrit jamais lui-meme dans le
 *     journal : la callable `journaliserEvenement` verifie l'auteur, le nom de
 *     l'action et le droit d'ecriture sur l'app.
 *
 * Les evenements du socle (connexions, reglages, acces, Lia) ne passent pas
 * par ici : les Functions les ecrivent d'elles-memes.
 */

/** Region des Cloud Functions. DOIT valoir celle de `functions/src/runtime.ts`. */
export const JOURNAL_FUNCTIONS_REGION = 'europe-west1';

/** Le nom de la callable qui ecrit un evenement d'app. */
export const CALLABLE_JOURNAL = 'journaliserEvenement';

/** Taille de page par defaut, et plafond. */
export const JOURNAL_PAGE_DEFAUT = 50;
export const JOURNAL_PAGE_MAX = 200;

/** Les filtres du journal. Tous facultatifs ; `from` inclus, `to` exclu. */
export interface FiltresJournal {
  category?: AuditCategory | undefined;
  actorUid?: string | undefined;
  appId?: string | undefined;
  from?: Date | undefined;
  to?: Date | undefined;
  /** Le curseur rendu par la page precedente (`next`). */
  cursor?: string | null | undefined;
  pageSize?: number | undefined;
}

/** Une page du journal, du plus recent au plus ancien. `next` nul : c'etait la derniere. */
export interface PageJournal {
  events: AuditEvent[];
  next: string | null;
}

/** Ce qu'une app note. L'auteur vient du jeton, jamais d'ici. */
export interface EvenementDApp {
  appId: string;
  /** Le verbe : minuscules, chiffres, tirets (`export`, `relance-envoyee`). */
  verbe: string;
  target?: { collection: string; id: string; door?: string; label?: string } | undefined;
  /** Precisions PLATES, scalaires courts, 1 Ko au plus. Jamais de texte saisi ni de contenu de fiche. */
  meta?: AuditMeta | undefined;
  outcome?: 'ok' | 'error' | undefined;
}

export interface JournalClient {
  list(filtres?: FiltresJournal): Promise<PageJournal>;
  /**
   * Rend l'id de l'evenement, ou `null` si le journal n'a pas pu l'ecrire
   * (Functions pas deployees, reseau) : un journal en panne ne casse pas le
   * geste de l'app. LEVE sur une demande invalide ou un droit manquant, que
   * l'app doit voir.
   */
  noter(evenement: EvenementDApp): Promise<string | null>;
}

/** Le curseur : l'instant exact (secondes et nanosecondes) et l'id du dernier evenement lu. */
export function encoderCurseur(at: Timestamp, id: string): string {
  return `${at.seconds}.${at.nanoseconds}:${id}`;
}

export function decoderCurseur(curseur: string): { at: Timestamp; id: string } | null {
  const trouve = /^(-?\d+)\.(\d+):([A-Za-z0-9_-]{1,128})$/.exec(curseur);
  if (!trouve) return null;
  return { at: new Timestamp(Number(trouve[1]), Number(trouve[2])), id: trouve[3] as string };
}

/** Les contraintes Firestore d'une page. Pur (hors `Timestamp`), pour les tests. */
export function contraintesDuJournal(filtres: FiltresJournal): QueryConstraint[] {
  const contraintes: QueryConstraint[] = [];
  if (filtres.category) contraintes.push(where('category', '==', filtres.category));
  if (filtres.actorUid) contraintes.push(where('actor.uid', '==', filtres.actorUid));
  if (filtres.appId) contraintes.push(where('target.appId', '==', filtres.appId));
  if (filtres.from) contraintes.push(where('at', '>=', Timestamp.fromDate(filtres.from)));
  if (filtres.to) contraintes.push(where('at', '<', Timestamp.fromDate(filtres.to)));
  // L'id departage deux evenements du meme instant : sans lui, le curseur en
  // sauterait un ou en repeterait un.
  contraintes.push(orderBy('at', 'desc'), orderBy(documentId(), 'desc'));
  if (filtres.cursor) {
    const curseur = decoderCurseur(filtres.cursor);
    if (curseur === null) throw new Error('Journal : curseur illisible.');
    contraintes.push(startAfter(curseur.at, curseur.id));
  }
  const taille = Math.min(Math.max(1, Math.floor(filtres.pageSize ?? JOURNAL_PAGE_DEFAUT)), JOURNAL_PAGE_MAX);
  contraintes.push(limit(taille));
  return contraintes;
}

function enDate(valeur: unknown): Date | null {
  return valeur instanceof Timestamp ? valeur.toDate() : null;
}

/** Un document lu, converti et valide ; `null` s'il ne respecte pas le format. */
export function evenementDuDocument(id: string, data: Record<string, unknown>): AuditEvent | null {
  const verdict = AuditEventSchema.safeParse({ ...data, id, at: enDate(data['at']), expiresAt: enDate(data['expiresAt']) });
  if (!verdict.success) {
    console.warn(`[@taki/sdk] journal : evenement ${id} illisible, ignore.`, verdict.error.issues[0]);
    return null;
  }
  return verdict.data;
}

const emulatorBoundFunctions = new WeakSet<Functions>();

export function createJournalClient(
  db: Firestore,
  app: FirebaseApp,
  orgId: string,
  emulator?: EmulatorEndpoint | undefined,
): JournalClient {
  const ref = collection(db, 'orgs', orgId, AUDIT_EVENTS_COLLECTION);
  let appeler: ReturnType<typeof httpsCallable<unknown, unknown>> | null = null;

  function callable() {
    if (appeler === null) {
      const functions = getFunctions(app, JOURNAL_FUNCTIONS_REGION);
      if (emulator && !emulatorBoundFunctions.has(functions)) {
        connectFunctionsEmulator(functions, emulator.host, emulator.port);
        emulatorBoundFunctions.add(functions);
      }
      appeler = httpsCallable<unknown, unknown>(functions, CALLABLE_JOURNAL);
    }
    return appeler;
  }

  return {
    async list(filtres = {}) {
      const contraintes = contraintesDuJournal(filtres);
      const snapshot = await getDocs(query(ref, ...contraintes));
      const events = snapshot.docs
        .map((entry) => evenementDuDocument(entry.id, entry.data()))
        .filter((event): event is AuditEvent => event !== null);
      const taille = Math.min(Math.max(1, Math.floor(filtres.pageSize ?? JOURNAL_PAGE_DEFAUT)), JOURNAL_PAGE_MAX);
      const dernier = snapshot.docs.at(-1);
      const at = dernier?.data()['at'];
      const next = snapshot.docs.length === taille && dernier && at instanceof Timestamp ? encoderCurseur(at, dernier.id) : null;
      return { events, next };
    },

    async noter(evenement) {
      const action = actionDApp(evenement.appId, evenement.verbe);
      if (action === null) {
        throw new Error(`Journal : « app.${evenement.appId}.${evenement.verbe} » n'est pas un nom d'action valide.`);
      }
      try {
        const reponse = await callable()({
          appId: evenement.appId,
          action,
          ...(evenement.target ? { target: evenement.target } : {}),
          ...(evenement.meta ? { meta: evenement.meta } : {}),
          ...(evenement.outcome ? { outcome: evenement.outcome } : {}),
        });
        const data = reponse.data as { id?: unknown } | null;
        return typeof data?.id === 'string' ? data.id : null;
      } catch (cause: unknown) {
        const code = String((cause as { code?: unknown })?.code ?? '').replace(/^functions\//, '');
        // Une Function absente rend aussi `not-found` : seule la fiche introuvable est une faute de l'app.
        const ficheIntrouvable = code === 'not-found' && /introuvable/i.test(String((cause as Error)?.message));
        if (code === 'invalid-argument' || code === 'permission-denied' || ficheIntrouvable) {
          throw cause;
        }
        console.warn('[@taki/sdk] journal : evenement non note.', cause);
        return null;
      }
    },
  };
}
